// useFaxDraft — autosave for the fax windows. The window hands over what it
// holds each render; a second after the last change, if there is anything
// worth keeping, it is written to `fax_drafts`. Closing the window is then
// harmless, however it happens.
//
//   const draft = useFaxDraft({ kind, clientId, initial, fields, meaningful, profile });
//   draft.status     'idle' | 'saving' | 'saved' | 'error'
//   draft.savedAt    Date of the last successful save
//   draft.draftId    null until the first save
//   draft.flush()    save now if anything is unsaved (call before closing)
//   draft.settle()   stop saving and wait for any save in flight → draftId
//                    (call before sending, so the send can clear the draft)
//   draft.resume()   start saving again (after a send that failed)
//   draft.discard()  stop saving and delete the draft with its staged files
//
// Saves run one at a time, so the first insert always lands before the
// updates that follow it and a draft can never be created twice.
import { useCallback, useEffect, useRef, useState } from 'react';
import { saveFaxDraft, discardFaxDraft } from '../lib/faxDrafts';

const DEBOUNCE_MS = 900;

export default function useFaxDraft({ kind, clientId, initial, fields, meaningful, profile }) {
  const [draftId, setDraftId] = useState(initial?.id || null);
  const [status, setStatus] = useState(initial?.id ? 'saved' : 'idle');
  const [savedAt, setSavedAt] = useState(initial?.updated_at ? new Date(initial.updated_at) : null);

  const json = JSON.stringify(fields);
  const idRef = useRef(initial?.id || null);
  const lastSaved = useRef(initial?.id ? json : null);   // a resumed draft is already saved as-is
  const latest = useRef({ json, fields, meaningful, clientId });
  latest.current = { json, fields, meaningful, clientId };
  const chain = useRef(Promise.resolve());
  const timer = useRef(null);
  const stopped = useRef(false);
  const profileRef = useRef(profile);
  profileRef.current = profile;

  const saveNow = useCallback(() => {
    chain.current = chain.current.then(async () => {
      const { json: j, fields: f, meaningful: m, clientId: c } = latest.current;
      if (stopped.current || !m || j === lastSaved.current) return;
      setStatus('saving');
      try {
        const row = await saveFaxDraft(idRef.current, { kind, client_id: c || null, ...f }, profileRef.current);
        if (!idRef.current) { idRef.current = row.id; setDraftId(row.id); }
        lastSaved.current = j;
        setSavedAt(new Date(row.updated_at || Date.now()));
        setStatus('saved');
      } catch (e) {
        console.error('fax draft save failed:', e);
        setStatus('error');
      }
    });
    return chain.current;
  }, [kind]);

  useEffect(() => {
    if (stopped.current || !meaningful || json === lastSaved.current) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(saveNow, DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [json, meaningful, saveNow]);

  // Leaving the page, or the window being torn down, still saves the last edit.
  useEffect(() => () => { clearTimeout(timer.current); if (!stopped.current) saveNow(); }, [saveNow]);

  const flush = useCallback(() => { clearTimeout(timer.current); return saveNow(); }, [saveNow]);

  const settle = useCallback(async () => {
    clearTimeout(timer.current);
    await saveNow();
    stopped.current = true;
    await chain.current;
    return idRef.current;
  }, [saveNow]);

  const resume = useCallback(() => { stopped.current = false; }, []);

  const discard = useCallback(async () => {
    clearTimeout(timer.current);
    stopped.current = true;
    await chain.current;
    if (idRef.current) await discardFaxDraft(idRef.current);
    idRef.current = null;
    setDraftId(null);
    setStatus('idle');
  }, []);

  return { draftId, status, savedAt, flush, settle, resume, discard };
}

// Footer text for the save state.
export const draftStatusText = (d) => {
  if (d.status === 'saving') return 'Saving draft…';
  if (d.status === 'error') return 'Draft not saved — check your connection';
  if (d.status === 'saved' && d.savedAt) return `Draft saved · ${d.savedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  return '';
};
